import { Box } from '@mui/material';
import {
  type LandingPageConfig,
  resolveLandingPageLogoHeight,
} from '@provision/landing-page-utils';
import { useMemo, useState } from 'react';
import { withOffBrandButton } from '../block-background';
import { COPY } from '../copy';
import { resolveCta } from '../cta';
import { useLayout, useLayoutStyles } from '../layouts/layout-context';
import { LayoutProvider } from '../layouts/layout-provider';
import { CtaButton } from '../primitives/cta-button';
import { LogoMarkIcon } from '../primitives/icons';
import { Wrap } from '../primitives/wrap';
import type { LandingPageProps } from '../types';
import { resolveHeaderColors } from './header-colors';

interface TopBarProps {
  settings: LandingPageConfig['settings'];
  logoUrl?: string;
  brandName?: string;
  plansBlockId?: string;
  faqBlockId?: string;
  onCta?: LandingPageProps['onCta'];
  pendingCta?: LandingPageProps['pendingCta'];
}

export const TopBar = ({
  settings,
  logoUrl,
  brandName,
  plansBlockId,
  faqBlockId,
  onCta,
  pendingCta,
}: TopBarProps) => {
  const layout = useLayout();
  const s = useLayoutStyles('chrome');
  const [logoBroken, setLogoBroken] = useState(false);
  const showLogoImage = Boolean(logoUrl) && !logoBroken;

  const logoHeight = resolveLandingPageLogoHeight(settings);
  const size = s.headerSize(logoHeight);
  const { backgroundColor, textColor } = settings.header ?? {};
  // Keyed on the strings, never `settings.header`: react-hook-form hands back a fresh object.
  const colors = useMemo(
    () => resolveHeaderColors({ backgroundColor, textColor }, layout.tokens),
    [backgroundColor, textColor, layout.tokens],
  );
  const ctaLayout = useMemo(
    () => (colors?.brandBlends ? withOffBrandButton(layout, colors.surface) : layout),
    [colors, layout],
  );

  // The links inherit the menu's colour; only their hover needs the header's own text colour.
  const navLink = [s.navLink, colors ? { '&:hover': { color: colors.text } } : {}];

  return (
    <Box
      component="header"
      sx={[
        s.header,
        colors?.background ? { backgroundColor: colors.background } : {},
        colors ? { color: colors.text, borderBottomColor: colors.line } : {},
      ]}
    >
      <Wrap>
        <Box sx={[s.headerInner, size.inner]} data-logo-height={logoHeight}>
          <Box component="a" href="#" sx={s.logo} aria-label={brandName}>
            {showLogoImage ? (
              <Box
                component="img"
                src={logoUrl}
                alt={brandName ?? ''}
                onError={() => setLogoBroken(true)}
                sx={[s.logoImage, size.logo]}
              />
            ) : (
              <>
                <Box sx={s.logoMark} aria-hidden="true">
                  <LogoMarkIcon />
                </Box>
                {brandName ? (
                  <Box component="span" sx={s.logoName}>
                    {brandName}
                  </Box>
                ) : null}
              </>
            )}
          </Box>
          <Box component="nav" sx={[s.nav, colors ? { color: colors.mutedText } : {}]}>
            {plansBlockId ? (
              <Box component="a" href={`#${plansBlockId}`} sx={navLink}>
                {COPY.navMemberships}
              </Box>
            ) : null}
            {faqBlockId ? (
              <Box component="a" href={`#${faqBlockId}`} sx={navLink}>
                {COPY.navFaq}
              </Box>
            ) : null}
            <LayoutProvider layout={ctaLayout}>
              <CtaButton
                target={resolveCta({ settings, onCta, pendingCta, blockId: 'top-bar' })}
                variant="brand"
                withArrow
              />
            </LayoutProvider>
          </Box>
        </Box>
      </Wrap>
    </Box>
  );
};
